import { ToJson } from '../interfaces/ToJson';
import { MAX_LENGTH_POSTAL_CODES } from '@/constants';

/**
 * Interface représentant une ville depuis l'API Geo
 */
export interface IGeoApiCity {
    nom: string;
    code: string;
    _score: number;
    codesPostaux: string[];
    departement: {
        code: string;
        nom: string;
    };
    centre: {
        type: string;
        coordinates: number[];
    };
}

/**
 * Interface représentant une ville
 */
export interface ICity {
	code: string;
    name: string;
	postal_codes: string[];
	department: string;
    coordinates_x: number;
    coordinates_y: number;
}

/**
 * Classe représentant une ville
 */
export class City implements ToJson<ICity> {
	private _code: string;
    private _name: string;
	private _postal_codes: string[];
	private _department: string;
    private _coordinates_x: number;
    private _coordinates_y: number;

    /**
     * Constructeur de la classe City
     * @param data Interface ICity
     */
    constructor(data: ICity) {
		this._code = data.code;
        this._name = data.name;
		this._postal_codes = data.postal_codes;
		this._department = data.department;
        this._coordinates_x = data.coordinates_x;
        this._coordinates_y = data.coordinates_y;
    }

    /**
     * Récupère la ville sous forme de JSON
     */
    toJson(): ICity {
        return {
			code: this._code,
            name: this._name,
			postal_codes: this._postal_codes,
			department: this._department,
            coordinates_x: this._coordinates_x,
            coordinates_y: this._coordinates_y
        };
    }

	/**
	 * Formatte les codes postaux d'une ville
	 * @example
	 * 			Une ville a 1 code postal → 00000
	 * 			Une ville a 3 codes postaux → 00000, 00001, 00002
	 * 			Une ville a 4 codes postaux ou plus → 00000, 00001, ..., 00003
	 */
	private formatPostalCodes(): string {
		if (this._postal_codes.length > MAX_LENGTH_POSTAL_CODES) {
			const lastCode = `${this._postal_codes[this._postal_codes.length - 1]}`;
			const formattedCodes = `${this._postal_codes.slice(0, MAX_LENGTH_POSTAL_CODES - 1).join(', ')}`;
			return `${formattedCodes}, ..., ${lastCode}`;
		}

		return this._postal_codes.join(', ');
	}

	/**
	 * Récupère le nom de la ville avec les codes postaux tronqués
	 * @example Paris, 75000, 75100, 75101, Paris → Paris, 75000, ..., 75101, Paris
	 */
	get nameWithTruncatedPostalCodes(): string {
		return `${this._name}, ${this.formatPostalCodes()}, ${this._department}`;
	}

	/**
	 * Récupère le code de la ville
	 */
	get code(): string {
		return this._code;
	}

    /**
	* Récupère le nom de la ville
     */
    get name(): string {
        return this._name;
    }

    /**
     * Récupère la coordonnée en longitude de la ville
     */
    get coordinates_x(): number {
        return this._coordinates_x;
    }

    /**
     * Récupère la coordonnée en latitude de la ville
     */
    get coordinates_y(): number {
        return this._coordinates_y;
    }
}
